import type React from 'react'
import { useState } from 'react'
import { brl } from '@impulso/shared'

// Gráfico de área (investimento) + linha (contatos), com eixos independentes.
export interface DailyPoint { date: Date | string; label: string; spend: number; contacts: number }

export function SpendContactsChart({ data, height = 220 }: { data: DailyPoint[]; height?: number }) {
  if (data.length < 2) return <div className="empty small">Ainda não há dados suficientes para o gráfico.</div>
  const [hover, setHover] = useState<number | null>(null)
  const w = 640
  const h = height
  const pad = { t: 12, r: 8, b: 26, l: 8 }
  const iw = w - pad.l - pad.r
  const ih = h - pad.t - pad.b
  const maxS = Math.max(...data.map((d) => d.spend)) * 1.15
  const maxC = Math.max(...data.map((d) => d.contacts)) * 1.3
  const x = (i: number) => pad.l + (i / (data.length - 1)) * iw
  const yS = (v: number) => pad.t + ih - (v / maxS) * ih
  const yC = (v: number) => pad.t + ih - (v / maxC) * ih

  const spendLine = data.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${yS(d.spend)}`).join(' ')
  const spendArea = `${spendLine} L${x(data.length - 1)},${pad.t + ih} L${x(0)},${pad.t + ih} Z`
  const contactLine = data.map((d, i) => `${i ? 'L' : 'M'}${x(i)},${yC(d.contacts)}`).join(' ')

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const rel = ((e.clientX - rect.left) / rect.width) * w
    const i = Math.round(((rel - pad.l) / iw) * (data.length - 1))
    setHover(Math.max(0, Math.min(data.length - 1, i)))
  }

  const hd = hover != null ? data[hover] : null

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" onMouseMove={onMove} onMouseLeave={() => setHover(null)} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="spendFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#5b3df5" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#5b3df5" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={pad.l} x2={w - pad.r} y1={pad.t + ih * (1 - f)} y2={pad.t + ih * (1 - f)} stroke="#eef0f3" vectorEffect="non-scaling-stroke" />
        ))}
        <path d={spendArea} fill="url(#spendFill)" />
        <path d={spendLine} fill="none" stroke="#5b3df5" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        <path d={contactLine} fill="none" stroke="#16a34a" strokeWidth="2" strokeDasharray="0" vectorEffect="non-scaling-stroke" />
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + ih} stroke="#9ca3af" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
      </svg>
      <div className="row between small muted" style={{ marginTop: -18, padding: '0 4px' }}>
        <span>{data[0].label}</span>
        <span>{data[Math.floor(data.length / 2)].label}</span>
        <span>{data[data.length - 1].label}</span>
      </div>
      {hd && (
        <div
          className="card"
          style={{ position: 'absolute', top: 0, left: `${(x(hover!) / w) * 100}%`, transform: `translateX(${hover! > data.length / 2 ? '-105%' : '5%'})`, padding: '10px 12px', pointerEvents: 'none', boxShadow: 'var(--shadow-lg)', fontSize: 12.5, minWidth: 150 }}
        >
          <div className="strong">{new Date(hd.date).toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' })}</div>
          <div className="row mt-8"><span className="status-dot" style={{ background: '#5b3df5' }} /> Investido <b style={{ marginLeft: 'auto' }}>{brl(hd.spend)}</b></div>
          <div className="row"><span className="status-dot" style={{ background: '#16a34a' }} /> Clientes <b style={{ marginLeft: 'auto' }}>{Number.isInteger(hd.contacts) ? hd.contacts : hd.contacts.toFixed(1).replace('.', ',')}</b></div>
        </div>
      )}
    </div>
  )
}

export function Sparkline({ values, color = '#5b3df5', width = 90, height = 28 }: { values: number[]; color?: string; width?: number; height?: number }) {
  if (values.length === 1) values = [values[0]!, values[0]!]
  if (!values.length) return <span className="muted small">—</span>
  const max = Math.max(...values, 1)
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * width},${height - 2 - (v / max) * (height - 4)}`).join(' ')
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

export function Donut({ parts, size = 120, thickness = 16, center }: { parts: Array<{ label: string; value: number; color: string }>; size?: number; thickness?: number; center?: React.ReactNode }) {
  if (!parts.some((p) => p.value > 0)) parts = [{ label: 'vazio', value: 1, color: '#eef0f3' }]
  const total = parts.reduce((s, p) => s + p.value, 0)
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  let acc = 0
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef0f3" strokeWidth={thickness} />
        {parts.map((p) => {
          const len = (p.value / total) * c
          const el = (
            <circle key={p.label} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={p.color} strokeWidth={thickness} strokeDasharray={`${len - 2} ${c - len + 2}`} strokeDashoffset={-acc} />
          )
          acc += len
          return el
        })}
      </svg>
      {center && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>{center}</div>}
    </div>
  )
}
